import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { BsTrash, BsPlus } from 'react-icons/bs';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import AnimatedModal from './AnimatedModal';
import { formatarCategoria } from './CriarProcesso/utils';
import api from '../../../api';

interface EditarEditaisModalProps {
  show: boolean;
  onClose: () => void;
  processo: any;
  onSuccess: () => void;
}

interface Edital {
  id?: string;
  nome: string;
  hasChamadas: boolean;
  url?: string;
}

const CATEGORIAS = [
  { value: 'LEGADO', label: 'Legado' },
  { value: 'ALUNO_TECNICO', label: 'Aluno Técnico' },
  { value: 'ALUNO_GRADUACAO', label: 'Aluno Graduação' },
  { value: 'ALUNO_POS', label: 'Aluno Pós' },
  { value: 'SERVIDOR', label: 'Servidor' },
  { value: 'PROCURADOR', label: 'Procurador' },
  { value: 'DOCENTE', label: 'Docente' }
];

const EditarEditaisModal: React.FC<EditarEditaisModalProps> = ({ 
  show, 
  onClose, 
  processo,
  onSuccess
}) => {
  const [editais, setEditais] = useState<Edital[]>([]);
  const [categoria, setCategoria] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (processo?.editais) {
      setEditais(processo.editais.map((e: any) => ({
        id: e.id,
        nome: e.nome,
        hasChamadas: e.hasChamadas,
        url: e.url || ''
      })));
      setCategoria(processo.editais[0]?.categoria || '');
    }
  }, [processo]);

  if (!show) return null;

  const handleEditalChange = (index: number, field: keyof Edital, value: string | boolean) => {
    setEditais(prev => prev.map((edital, i) => 
      i === index ? { ...edital, [field]: value } : edital
    ));
  };

  const addEdital = () => {
    setEditais(prev => [...prev, { nome: '', hasChamadas: true, url: '' }]);
  };

  const removeEdital = (index: number) => {
    if (editais.length > 1) {
      setEditais(prev => prev.filter((_, i) => i !== index));
    } else {
      toast.error('O processo deve ter pelo menos um edital.');
    }
  };

  const handleSave = async () => {
    const editaisValidos = editais.filter(e => e.nome.trim());
    
    if (editaisValidos.length === 0) {
      toast.error('Preencha pelo menos um edital.');
      return;
    }

    if (!categoria) {
      toast.error('Selecione uma categoria.');
      return;
    }

    try {
      setLoading(true);
      
      // Atualiza categoria se mudou
      if (categoria !== processo.editais[0]?.categoria) {
        await api.patch(`/processos/${processo.id}/editais/categoria`, { categoria });
      }

      // Atualiza editais
      const editaisParaEnviar = editaisValidos.map(e => ({
        id: e.id || null,
        nome: e.nome.trim(),
        hasChamadas: e.hasChamadas,
        url: e.url?.trim() || undefined
      }));

      await api.put(`/processos/${processo.id}/editais`, editaisParaEnviar);

      toast.success('Editais atualizados com sucesso!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedModal isOpen={show} onClose={onClose}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        minWidth: '600px',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <div className="modal-header" style={{
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '16px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            Editar Editais do Processo
          </h3>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              Categoria dos Editais
            </label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              disabled={!processo.nomeEditavel}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                cursor: processo.nomeEditavel ? 'pointer' : 'not-allowed'
              }}
            >
              <option value="">Selecione uma categoria</option>
              {CATEGORIAS.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
            {!processo.nomeEditavel && (
              <small style={{ color: '#6c757d', fontSize: '12px' }}>Disponível para edição em processos não finalizados ou cancelados</small>
            )}
          </div>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#495057'
            }}>
              Editais
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {editais.map((edital, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa'
                }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={edital.nome}
                      onChange={(e) => handleEditalChange(index, 'nome', e.target.value)}
                      placeholder={`Nome do edital ${index + 1}`}
                      disabled={!processo.nomeEditavel}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        fontSize: '14px',
                        backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                        cursor: processo.nomeEditavel ? 'text' : 'not-allowed'
                      }}
                    />
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      fontSize: '13px', 
                      whiteSpace: 'nowrap',
                      minWidth: '140px'
                    }}>
                      <input
                        type="checkbox"
                        checked={edital.hasChamadas}
                        onChange={(e) => handleEditalChange(index, 'hasChamadas', e.target.checked)}
                        disabled={!processo.nomeEditavel}
                        style={{ cursor: processo.nomeEditavel ? 'pointer' : 'not-allowed' }}
                      />
                      Prevê Chamadas
                    </label>
                    {processo.nomeEditavel && (
                      <button
                        onClick={() => removeEdital(index)}
                        disabled={editais.length === 1}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #dc3545',
                          borderRadius: '4px',
                          backgroundColor: editais.length === 1 ? '#f8f9fa' : 'white',
                          color: editais.length === 1 ? '#6c757d' : '#dc3545',
                          cursor: editais.length === 1 ? 'not-allowed' : 'pointer',
                          fontSize: '14px'
                        }}
                        title={editais.length === 1 ? "Processo deve ter pelo menos um edital" : "Remover edital"}
                      >
                        <BsTrash />
                      </button>
                    )}
                  </div>
                  <input
                    type="url"
                    value={edital.url || ''}
                    onChange={(e) => handleEditalChange(index, 'url', e.target.value)}
                    placeholder="URL do edital (opcional)"
                    disabled={!processo.nomeEditavel}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '13px',
                      backgroundColor: processo.nomeEditavel ? 'white' : '#f8f9fa',
                      cursor: processo.nomeEditavel ? 'text' : 'not-allowed'
                    }}
                  />
                </div>
              ))}
              
              {processo.nomeEditavel && (
                <button
                  onClick={addEdital}
                  style={{
                    padding: '10px 16px',
                    border: '2px dashed #0d6efd',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#0d6efd',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f7ff'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <BsPlus size={20} /> Adicionar Edital
                </button>
              )}
            </div>
          </div>

          {!processo.nomeEditavel && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '13px',
              color: '#856404'
            }}>
              ⚠️ Este processo não pode mais ser editado devido ao seu status atual.
            </div>
          )}
        </div>

        <div className="modal-footer" style={{
          borderTop: '1px solid #e9ecef',
          paddingTop: '16px',
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #6c757d',
              borderRadius: '4px',
              backgroundColor: 'white',
              color: '#6c757d',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Cancelar
          </button>
          {processo.nomeEditavel && (
            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                padding: '8px 16px',
                border: '1px solid #0d6efd',
                borderRadius: '4px',
                backgroundColor: loading ? '#6c757d' : '#0d6efd',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          )}
        </div>
      </div>
    </AnimatedModal>
  );
};

export default EditarEditaisModal;
